import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getCurrentCountries } from "@insite/client-framework/Services/WebsiteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { StateModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useConstructor from "@preferredMedical/Components/Misc/UseConstructor";
import { TradeshowFormRequest } from "@preferredMedical/Models/CustomForms/Tradeshow/TradeshowFormRequest";
import { sendTradeshowForm } from "@preferredMedical/Services/CustomFormsService";
import validateReCaptcha from "@preferredMedical/Store/Components/Recapcha/Handlers/ValidateReCaptcha";
import parse from "html-react-parser";
import React, { RefObject, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: TradeShowFormStyles;
    emailRecipients: string;
    widgetSettings: {
        backgroundColor: string;
        introductoryText: string;
        referralOptions: string;
        businessTypes: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    countriesPromise: getCurrentCountries({ expand: ["states"] }),
});

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

export interface TradeShowFormStyles {
    container?: GridContainerProps;
    subContainer?: GridContainerProps;
    subContainerHeader?: TypographyProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;

    introductoryText?: TypographyProps;
    noticeText?: TypographyProps;

    reCaptchaGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
}

export const productShareLinkStyles: TradeShowFormStyles = {
    container: {
        gap: 10,
    },
    subContainer: {
        gap: 10,
        css: css`
            margin-bottom: 15px;
            text-align: left;
            padding-left: 20px;
            padding-right: 20px;
            &:first-child {
                padding-top: 20px;
            }
        `,
    },
    subContainerHeader: {
        weight: 600,
        size: "1.25em",
    },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding-left: 5px;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding-left: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-basis: 100%;
                            max-width: 100%;
                        `,
                        css`
                            flex-basis: 100%;
                            max-width: 100%;
                        `,
                        css`
                            flex-basis: 50%;
                            max-width: 50%;
                        `,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    introductoryText: {
        css: css`
            font-size: 1.5em;
            text-align: center;
        `,
    },
    noticeText: {
        css: css`
            text-align: center;
            width: 100%;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
            margin-bottom: 10px;
            margin-left: 0px;
        `,
    },
};

const TradeshowForm = ({
    emailRecipients,
    validateReCaptcha,
    extendedStyles,
    countriesPromise,
    widgetSettings,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productShareLinkStyles, extendedStyles));
    const [inProgress, setInProgress] = React.useState(false);
    const [states, setStates] = React.useState<StateModel[]>([]);
    const [formData, setFormData] = React.useState({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        businessType: "",
        state: "",
        referral: "",
        interests: "",
    });
    const [dropdownsData, setDropdownsData] = React.useState({
        businessTypeIndex: 0,
        referralIndex: 0,
    });
    const [formError, setFormErrors] = React.useState({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        businessType: "",
        state: "",
        referral: "",
    });
    const allReferralOptions = ["Select One"].concat(
        widgetSettings.referralOptions ? widgetSettings.referralOptions.split("\n") : [],
    );
    const allBusinessTypes = ["Select One"].concat(
        widgetSettings.businessTypes ? widgetSettings.businessTypes.split("\n") : [],
    );

    const fetchUSStates = () => {
        countriesPromise.then(model => {
            if (model.countries) {
                for (let i = 0; i < model.countries.length; i++) {
                    const country = model.countries[i];
                    if (country && country.abbreviation === "US" && country.states) {
                        setStates(country.states);
                        setFormData({ ...formData, state: country.states[0].abbreviation });
                    }
                }
            }
        });
    };

    const requiredFieldMessage = translate("This is a required field.");
    const emailFieldMessage = translate("Enter a valid email address.");
    const emailRegEx = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const checkForErrors = () => {
        let encounteredError = false;
        const errors = {
            firstName: "",
            lastName: "",
            email: "",
            company: "",
            businessType: "",
            state: "",
            referral: "",
        };

        if (!formData.email || formData.email === "") {
            errors.email = requiredFieldMessage;
            encounteredError = true;
        } else if (!emailRegEx.test(formData.email)) {
            errors.email = emailFieldMessage;
            encounteredError = true;
        } else {
            errors.email = "";
        }

        if (!formData.firstName || formData.firstName === "") {
            errors.firstName = requiredFieldMessage;
            encounteredError = true;
        } else {
            errors.firstName = "";
        }

        if (!formData.lastName || formData.lastName === "") {
            errors.lastName = requiredFieldMessage;
            encounteredError = true;
        } else {
            errors.lastName = "";
        }

        if (!formData.company || formData.company === "") {
            errors.company = requiredFieldMessage;
            encounteredError = true;
        } else {
            errors.company = "";
        }

        if (dropdownsData.businessTypeIndex === 0) {
            errors.businessType = requiredFieldMessage;
            encounteredError = true;
        } else {
            errors.businessType = "";
        }

        if (dropdownsData.referralIndex === 0) {
            errors.referral = requiredFieldMessage;
            encounteredError = true;
        } else {
            errors.referral = "";
        }

        setFormErrors(errors);

        return encounteredError;
    };

    const resetFields = () => {
        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            company: "",
            businessType: "",
            state: "",
            referral: "",
            interests: "",
        });
        setDropdownsData({
            businessTypeIndex: 0,
            referralIndex: 0,
        });
        setFormErrors({
            firstName: "",
            lastName: "",
            email: "",
            company: "",
            businessType: "",
            state: "",
            referral: "",
        });
        setInProgress(false);
    };

    const submitButtonClickHandler = async () => {
        if (checkForErrors()) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "TradeshowForm",
            recaptchaRef,
        });
        
        if (!isReCaptchaValid) {
            return;
        }

        setInProgress(true);

        const model = createModel();
        sendTradeshowForm(model);
        resetFields();
        const confirmationPageUrl = `Tradeshow/RequestConfirmation`;
        if (confirmationPageUrl) {
            window.location.href = confirmationPageUrl;
        }
    };

    const createModel = () => {
        const model = new TradeshowFormRequest();
        model.firstName = formData.firstName;
        model.lastName = formData.lastName;
        model.email = formData.email;
        model.company = formData.company;
        model.businessType = formData.businessType;
        model.state = formData.state;
        model.referral = formData.referral;
        model.interests = formData.interests;
        model.emailTos = emailRecipients.split("\n");
        return model;
    };

    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, firstName: event.target.value });
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, lastName: event.target.value });
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setFormData({ ...formData, email: newValue });
        if (!emailRegEx.test(newValue)) {
            setFormErrors({ ...formError, email: emailFieldMessage });
        } else {
            setFormErrors({ ...formError, email: "" });
        }
    };

    const companyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, company: event.target.value });
    };

    const stateChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const index = Number(event.target.value);
        setFormData({ ...formData, state: states[index].abbreviation });
    };

    const referralChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const index = Number(event.target.value);
        setDropdownsData({ ...dropdownsData, referralIndex: index });
        setFormData({ ...formData, referral: allReferralOptions[index] });
    };

    const businessTypeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const index = Number(event.target.value);
        setDropdownsData({ ...dropdownsData, businessTypeIndex: index });
        setFormData({ ...formData, businessType: allBusinessTypes[index] });
    };

    const interestsChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, interests: event.target.value });
    };

    const stateOptions = states.map((option, index) => {
        return (
            <option key={`state${option.abbreviation}`} value={index}>
                {option.name}
            </option>
        );
    });
    const referralOptions = allReferralOptions.map((option, index) => {
        return (
            <option key={`classOfTrade_${option}`} value={index}>
                {option}
            </option>
        );
    });
    const businessTypeOptions = allBusinessTypes.map((option, index) => {
        return (
            <option key={`userType_${option}`} value={index}>
                {option}
            </option>
        );
    });

    useConstructor(() => {
        fetchUSStates();
        if (styles.container) {
            styles.container.css = css`
                padding: 10px;
                background-color: ${widgetSettings.backgroundColor};
                input,
                option,
                select {
                    color: black !important;
                }
            `;
        }
    });

    return (
        <>
            <GridContainer {...styles.container}>
                <GridContainer {...styles.subContainer}>
                    <GridItem {...styles.oneLinerGridItem}>
                        <Typography {...styles.introductoryText}>
                            {parse(widgetSettings.introductoryText, parserOptions)}
                        </Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer {...styles.subContainer}>
                    <GridItem {...styles.twoLinerGridItem}>
                        <TextField
                            label={translate("First Name")}
                            value={formData.firstName}
                            onChange={firstNameChangeHandler}
                            error={formError.firstName}
                            required={true}
                        />
                    </GridItem>
                    <GridItem {...styles.twoLinerGridItem}>
                        <TextField
                            label={translate("Last Name")}
                            value={formData.lastName}
                            onChange={lastNameChangeHandler}
                            error={formError.lastName}
                            required={true}
                        />
                    </GridItem>
                    <GridItem {...styles.twoLinerGridItem}>
                        <TextField
                            label={translate("Email")}
                            value={formData.email}
                            onChange={emailChangeHandler}
                            error={formError.email}
                            required={true}
                        />
                    </GridItem>
                    <GridItem {...styles.twoLinerGridItem}>
                        <TextField
                            label={translate("Company")}
                            value={formData.company}
                            onChange={companyChangeHandler}
                            error={formError.company}
                            required={true}
                        />
                    </GridItem>
                    <GridItem {...styles.twoLinerGridItem}>
                        <Select
                            label={translate("State")}
                            required
                            placeholder="Select"
                            error={formError.state}
                            onChange={stateChangeHandler}
                        >
                            {stateOptions}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.twoLinerGridItem}>
                        <Select
                            label={translate("How did you hear about us?")}
                            required
                            placeholder="Select"
                            error={formError.referral}
                            onChange={referralChangeHandler}
                        >
                            {referralOptions}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.oneLinerGridItem}>
                        <Select
                            label={translate("Type Of Business")}
                            required
                            placeholder="Select"
                            error={formError.businessType}
                            onChange={businessTypeChangeHandler}
                        >
                            {businessTypeOptions}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.oneLinerGridItem}>
                        <TextField
                            label={translate("What are you intrested in?")}
                            value={formData.interests}
                            onChange={interestsChangeHandler}
                        />
                    </GridItem>
                    <GridItem {...styles.oneLinerGridItem}>
                        <Typography {...styles.noticeText}>Contact Your Sales Representative Today.</Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer {...styles.subContainer}>
                    <GridItem {...styles.oneLinerGridItem}>
                        <Button
                            {...styles.submitButton}
                            onClick={() => {
                                submitButtonClickHandler();
                            }}
                            disabled={inProgress}
                        >
                            {translate("Submit")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </GridContainer>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(TradeshowForm);
